import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useErrorText } from "@/components/error-message";
import { Button } from "@/components/ui/button";
import { LoginPage } from "@/features/auth/login-page";
import { meQuery } from "@/features/auth/queries";
import { LegalPage } from "@/features/legal/legal-page";
import { OnboardingPage } from "@/features/onboarding/onboarding-page";
import { AppShell } from "@/features/tenant/app-shell";
import { DashboardPage } from "@/features/tenant/dashboard-page";
import { overviewQuery } from "@/features/tenant/queries";
import { useI18n } from "@/i18n";
import { ApiError } from "@/lib/api";
import { reportError } from "@/lib/report-error";

interface RouterContext {
  queryClient: QueryClient;
}

function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  const text = useErrorText();
  const { t } = useI18n();
  useEffect(() => {
    // API problems are logged by the server; anything else here is a bug in the app.
    if (!(error instanceof ApiError)) reportError(error);
  }, [error]);
  return (
    <div className="mx-auto flex min-h-[50dvh] max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-sm text-muted-foreground">{text(error)}</p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={reset}>
          {t("common.retry")}
        </Button>
        <Link
          to="/"
          className="inline-flex h-10 items-center px-4 text-sm font-medium text-primary"
        >
          {t("nav.dashboard")}
        </Link>
      </div>
    </div>
  );
}

function Pending() {
  return (
    <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
      <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: RouteError,
  pendingComponent: Pending,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: "/login" });
    const first = me.memberships.find((m) => m.tenantStatus === "active");
    // Zeetabyte staff without a business of their own start in the admin.
    if (!first && me.platformRole) throw redirect({ to: "/admin" });
    if (!first) throw redirect({ to: "/onboarding" });
    throw redirect({ to: "/t/$tenantId", params: { tenantId: first.tenantId } });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { email?: string; error?: string } => ({
    ...(typeof search.email === "string" ? { email: search.email.slice(0, 254) } : {}),
    ...(typeof search.error === "string" ? { error: search.error.slice(0, 40) } : {}),
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: "/login" });
  },
  component: OnboardingPage,
});

const tenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$tenantId",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: "/login" });
  },
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(overviewQuery(params.tenantId));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) throw redirect({ to: "/login" });
      if (error instanceof ApiError && error.status === 404) throw redirect({ to: "/" });
      throw error;
    }
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/",
  component: DashboardPage,
});

/* Feature screens load on demand so the first paint stays small (NFR-PERF-02). */

const sessionsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/sessions",
  component: lazyRouteComponent(() => import("@/features/tenant/sessions-page"), "SessionsPage"),
});

const posRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/pos",
  component: lazyRouteComponent(() => import("@/features/pos/pos-page"), "PosPage"),
});

const salesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/sales",
  component: lazyRouteComponent(() => import("@/features/sales/sales-page"), "SalesPage"),
});

const saleRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/sales/$saleId",
  component: lazyRouteComponent(
    () => import("@/features/sales/sale-detail-page"),
    "SaleDetailPage",
  ),
});

const productsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/products",
  component: lazyRouteComponent(() => import("@/features/catalog/products-page"), "ProductsPage"),
});

const stockRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/stock",
  component: lazyRouteComponent(() => import("@/features/stock/stock-page"), "StockPage"),
});

const purchasesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/purchases",
  component: lazyRouteComponent(
    () => import("@/features/purchases/purchases-page"),
    "PurchasesPage",
  ),
});

const purchaseNewRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/purchases/new",
  component: lazyRouteComponent(
    () => import("@/features/purchases/purchase-new-page"),
    "PurchaseNewPage",
  ),
});

const purchaseRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/purchases/$purchaseId",
  component: lazyRouteComponent(
    () => import("@/features/purchases/purchase-detail-page"),
    "PurchaseDetailPage",
  ),
});

const customersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/customers",
  component: lazyRouteComponent(() => import("@/features/parties/routes"), "CustomersPage"),
});

const customerRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/customers/$partyId",
  component: lazyRouteComponent(() => import("@/features/parties/routes"), "CustomerDetailPage"),
});

const suppliersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/suppliers",
  component: lazyRouteComponent(() => import("@/features/parties/routes"), "SuppliersPage"),
});

const supplierRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/suppliers/$partyId",
  component: lazyRouteComponent(() => import("@/features/parties/routes"), "SupplierDetailPage"),
});

const accountsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/accounts",
  component: lazyRouteComponent(() => import("@/features/money/accounts-page"), "AccountsPage"),
});

const expensesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/expenses",
  component: lazyRouteComponent(() => import("@/features/expenses/expenses-page"), "ExpensesPage"),
});

const wholesaleRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/wholesale",
  component: lazyRouteComponent(
    () => import("@/features/wholesale/wholesale-page"),
    "WholesalePage",
  ),
});

const loyaltyRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/loyalty",
  component: lazyRouteComponent(() => import("@/features/loyalty/loyalty-page"), "LoyaltyPage"),
});

const quotationsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/quotations",
  component: lazyRouteComponent(
    () => import("@/features/quotations/quotations-page"),
    "QuotationsPage",
  ),
});

const quotationNewRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/quotations/new",
  component: lazyRouteComponent(
    () => import("@/features/quotations/quotation-new-page"),
    "QuotationNewPage",
  ),
});

const quotationRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/quotations/$quotationId",
  component: lazyRouteComponent(
    () => import("@/features/quotations/quotation-page"),
    "QuotationPage",
  ),
});

const reportsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/reports",
  component: lazyRouteComponent(() => import("@/features/reports/reports-page"), "ReportsPage"),
});

const vatRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/vat",
  component: lazyRouteComponent(() => import("@/features/vat/vat-page"), "VatPage"),
});

const settingsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/settings",
  component: lazyRouteComponent(() => import("@/features/settings/settings-page"), "SettingsPage"),
});

const dataExportRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/settings/data",
  component: lazyRouteComponent(() => import("@/features/data-export/data-page"), "DataPage"),
});

const labelsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/labels",
  validateSearch: (search: Record<string, unknown>): { items?: string } =>
    typeof search.items === "string" ? { items: search.items } : {},
  component: lazyRouteComponent(() => import("@/features/labels/labels-page"), "LabelsPage"),
});

const printerRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/settings/printer",
  component: lazyRouteComponent(() => import("@/features/printing/printer-page"), "PrinterPage"),
});

const teamRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/team",
  component: lazyRouteComponent(() => import("@/features/team/team-page"), "TeamPage"),
});

const billingRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/billing",
  component: lazyRouteComponent(() => import("@/features/billing/billing-page"), "BillingPage"),
});

const ordersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/orders",
  component: lazyRouteComponent(() => import("@/features/orders/orders-page"), "OrdersPage"),
});

const orderRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/orders/$orderId",
  validateSearch: (search: Record<string, unknown>): { mode?: "live" | "test" } =>
    search.mode === "test" ? { mode: "test" } : {},
  component: lazyRouteComponent(
    () => import("@/features/orders/order-detail-page"),
    "OrderDetailPage",
  ),
});

const storefrontRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/storefront",
  component: lazyRouteComponent(
    () => import("@/features/storefront/storefront-page"),
    "StorefrontPage",
  ),
});

const couriersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/couriers",
  component: lazyRouteComponent(() => import("@/features/couriers/couriers-page"), "CouriersPage"),
});

const developersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/developers",
  component: lazyRouteComponent(
    () => import("@/features/developers/developers-page"),
    "DevelopersPage",
  ),
});

const apiDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/developers/api",
  component: lazyRouteComponent(() => import("@/features/developers/api-docs-page"), "ApiDocsPage"),
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: "/login" });
  },
  component: lazyRouteComponent(() => import("@/features/admin/layout"), "AdminLayout"),
});

/** One page per admin section, so each can be linked to and bookmarked. */
const adminPage = <P extends string>(
  path: P,
  load: () => Promise<Record<string, unknown>>,
  name: string,
) =>
  createRoute({
    getParentRoute: () => adminRoute,
    path,
    component: lazyRouteComponent(load as never, name as never),
  });

const adminOverviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  // Error alert emails from older releases link to /admin?errors=<fingerprint>.
  beforeLoad: ({ location }) => {
    const fingerprint = new URLSearchParams(location.searchStr).get("errors");
    if (fingerprint)
      throw redirect({ to: "/admin/errors", search: { errors: fingerprint } as never });
  },
  component: lazyRouteComponent(() => import("@/features/admin/overview"), "OverviewPage"),
});
const adminBusinessesRoute = adminPage(
  "/businesses",
  () => import("@/features/admin/businesses"),
  "BusinessesPage",
);
const ADMIN_BUSINESS_TABS = [
  "summary",
  "plan",
  "members",
  "billing",
  "sms",
  "website",
  "integrations",
  "support",
  "audit",
  "danger",
] as const;
const adminBusinessRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/businesses/$tenantId",
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: (typeof ADMIN_BUSINESS_TABS)[number] } =>
    typeof search.tab === "string" &&
    (ADMIN_BUSINESS_TABS as readonly string[]).includes(search.tab)
      ? { tab: search.tab as (typeof ADMIN_BUSINESS_TABS)[number] }
      : {},
  component: lazyRouteComponent(
    () => import("@/features/admin/business/business-page"),
    "BusinessPage",
  ),
});
const adminUsersRoute = adminPage(
  "/users",
  () => import("@/features/admin/user/users-page"),
  "UsersPage",
);
const adminUserRoute = adminPage(
  "/users/$userId",
  () => import("@/features/admin/user/user-page"),
  "UserPage",
);
const adminBillingRoute = adminPage(
  "/billing",
  () => import("@/features/admin/billing/billing-page"),
  "BillingPage",
);
const adminSupportRoute = adminPage(
  "/support",
  () => import("@/features/admin/admin-security"),
  "SupportPage",
);
const adminPlansRoute = adminPage("/plans", () => import("@/features/admin/plans"), "PlansPage");
const adminCatalogsRoute = adminPage(
  "/catalogs",
  () => import("@/features/admin/catalogs"),
  "CatalogsPage",
);
const adminBusinessTypesRoute = adminPage(
  "/business-types",
  () => import("@/features/admin/settings/business-types-page"),
  "BusinessTypesPage",
);
const adminAnnouncementsRoute = adminPage(
  "/announcements",
  () => import("@/features/admin/settings/announcements-page"),
  "AnnouncementsPage",
);
const adminMaintenanceRoute = adminPage(
  "/maintenance",
  () => import("@/features/admin/settings/maintenance-page"),
  "MaintenancePage",
);
const adminBarcodesRoute = adminPage(
  "/barcodes",
  () => import("@/features/admin/settings/barcodes-page"),
  "BarcodesPage",
);
const adminAiRoute = adminPage("/ai", () => import("@/features/admin/settings/ai-page"), "AiPage");
const adminSecuritySettingsRoute = adminPage(
  "/security",
  () => import("@/features/admin/settings/security-page"),
  "SecurityPage",
);
const adminStaffRoute = adminPage(
  "/staff",
  () => import("@/features/admin/admin-security"),
  "StaffPage",
);
const adminSignInsRoute = adminPage(
  "/signins",
  () => import("@/features/admin/admin-security"),
  "SignInsPage",
);
const adminAuditRoute = adminPage("/audit", () => import("@/features/admin/logs"), "AuditPage");
const adminErrorsRoute = adminPage("/errors", () => import("@/features/admin/logs"), "ErrorsPage");
const adminAccountRoute = adminPage(
  "/account",
  () => import("@/features/admin/account"),
  "AccountPage",
);

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: () => <LegalPage doc="terms" />,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: () => <LegalPage doc="privacy" />,
});

const routeTree = rootRoute.addChildren([
  adminRoute.addChildren([
    adminOverviewRoute,
    adminBusinessesRoute,
    adminBusinessRoute,
    adminUsersRoute,
    adminUserRoute,
    adminBillingRoute,
    adminSupportRoute,
    adminPlansRoute,
    adminCatalogsRoute,
    adminBusinessTypesRoute,
    adminAnnouncementsRoute,
    adminMaintenanceRoute,
    adminSecuritySettingsRoute,
    adminAiRoute,
    adminBarcodesRoute,
    adminStaffRoute,
    adminSignInsRoute,
    adminAuditRoute,
    adminErrorsRoute,
    adminAccountRoute,
  ]),
  apiDocsRoute,
  termsRoute,
  privacyRoute,
  indexRoute,
  loginRoute,
  onboardingRoute,
  tenantRoute.addChildren([
    dashboardRoute,
    sessionsRoute,
    posRoute,
    salesRoute,
    saleRoute,
    productsRoute,
    stockRoute,
    purchasesRoute,
    purchaseNewRoute,
    purchaseRoute,
    customersRoute,
    customerRoute,
    suppliersRoute,
    supplierRoute,
    accountsRoute,
    expensesRoute,
    wholesaleRoute,
    loyaltyRoute,
    quotationsRoute,
    quotationNewRoute,
    quotationRoute,
    reportsRoute,
    vatRoute,
    settingsRoute,
    dataExportRoute,
    printerRoute,
    labelsRoute,
    teamRoute,
    billingRoute,
    ordersRoute,
    orderRoute,
    storefrontRoute,
    couriersRoute,
    developersRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPendingMs: 300,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
